import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "toolbar"]

  connect() {
    console.log("Trix editor controller connected")
    
    // Initialize immediately to avoid flicker
    this.initializeEditor()
    
    // Register event listeners
    this.registerEventListeners()
  }
  
  disconnect() {
    // Clean up event listeners
    this.removeEventListeners()
  }
  
  registerEventListeners() {
    document.addEventListener("trix-initialize", this.onTrixInitialize.bind(this))
    document.addEventListener("trix-focus", this.onTrixFocus.bind(this))
    document.addEventListener("trix-blur", this.onTrixBlur.bind(this))
    document.addEventListener("trix-file-accept", this.onTrixFileAccept.bind(this))
    document.addEventListener("trix-attachment-add", this.onTrixAttachmentAdd.bind(this))
    document.addEventListener("turbo:load", this.onTurboLoad.bind(this))
    
    // Custom event to force a refresh of the editor
    this.element.addEventListener("trix-editor:reconnect", this.initializeEditor.bind(this))
  }
  
  removeEventListeners() {
    document.removeEventListener("trix-initialize", this.onTrixInitialize.bind(this))
    document.removeEventListener("trix-focus", this.onTrixFocus.bind(this))
    document.removeEventListener("trix-blur", this.onTrixBlur.bind(this))
    document.removeEventListener("trix-file-accept", this.onTrixFileAccept.bind(this))
    document.removeEventListener("trix-attachment-add", this.onTrixAttachmentAdd.bind(this))
    document.removeEventListener("turbo:load", this.onTurboLoad.bind(this))
    this.element.removeEventListener("trix-editor:reconnect", this.initializeEditor.bind(this))
  }
  
  initializeEditor() {
    // Process immediately without delay
    console.log("Initializing Trix editor", this.element)
    
    // Get all Trix editors and toolbars
    const editors = this.element.querySelectorAll("trix-editor")
    const toolbars = this.element.querySelectorAll("trix-toolbar")
    
    // Fix styling for each editor
    editors.forEach(editor => this.styleEditor(editor))
    
    // Fix button labels in each toolbar
    toolbars.forEach(toolbar => this.fixToolbarButtons(toolbar))
    
    // Fix attachment UI buttons (cancel/delete)
    this.fixAttachmentButtons()
    
    // Add mutation observer to catch dynamically added buttons
    this.setupMutationObserver()
    
    console.log(`Initialized ${editors.length} Trix editors`)
  }
  
  styleEditor(editor) {
    // Add class to identify processed editors
    editor.classList.add("trix-editor-initialized")
  }
  
  fixToolbarButtons(toolbar) {
    console.log("Fixing toolbar buttons for", toolbar)
    
    // Log all buttons to help identify the code button
    const allButtons = toolbar.querySelectorAll("button");
    allButtons.forEach(button => {
      console.log("Button:", {
        element: button,
        classes: Array.from(button.classList),
        attribute: button.getAttribute('data-trix-attribute'),
        action: button.getAttribute('data-trix-action'),
        html: button.innerHTML
      });
    });
    
    // Get all buttons within the toolbar
    const buttons = toolbar.querySelectorAll(".trix-button")
    console.log(`Found ${buttons.length} buttons to fix`)
    
    // Process each button to fix labels and icons
    buttons.forEach(button => {
      // Force hide any text nodes
      this.clearTextContent(button)
      
      // Apply fallback icon
      this.applyFallbackIcon(button)
    })
  }
  
  setupMutationObserver() {
    // Watch for dynamically added elements like attachment UI
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            // Check if this is a Trix attachment UI
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.querySelector('.trix-button--cancel, .trix-button--delete')) {
                this.fixAttachmentButtons();
              }
            }
          });
        }
      });
    });
    
    // Start observing the document body for changes
    observer.observe(document.body, { childList: true, subtree: true });
  }
  
  fixAttachmentButtons() {
    // Style Cancel buttons
    document.querySelectorAll('.trix-button--cancel').forEach(button => {
      console.log("Found Cancel button:", button);
      if (!button.classList.contains('styled')) {
        button.classList.add('styled', 'trix-button--cancel');
        button.style.backgroundColor = 'white';
        button.style.color = '#374151'; // gray-700
        button.style.border = '1px solid #d1d5db'; // gray-300
        button.style.borderRadius = '0.375rem';
        button.style.padding = '0.5rem 1rem';
        button.style.fontSize = '0.875rem';
        button.style.fontWeight = '500';
        button.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
      }
    });
    
    // Style Delete buttons
    document.querySelectorAll('.trix-button--delete').forEach(button => {
      console.log("Found Delete button:", button);
      if (!button.classList.contains('styled')) {
        button.classList.add('styled', 'trix-button--delete');
        button.style.backgroundColor = '#dc2626'; // red-600
        button.style.color = 'white';
        button.style.border = '1px solid transparent';
        button.style.borderRadius = '0.375rem';
        button.style.padding = '0.5rem 1rem';
        button.style.fontSize = '0.875rem';
        button.style.fontWeight = '500';
        button.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
      }
    });
  }
  
  clearTextContent(button) {
    // Remove any text directly in the button
    Array.from(button.childNodes).forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = ''
      }
    })
    
    // Hide any spans that might contain text
    const spans = button.querySelectorAll('span:not(.trix-fallback-icon)')
    spans.forEach(span => {
      span.style.display = 'none'
    })
  }
  
  applyFallbackIcon(button) {
    // Get button attributes
    const attribute = button.getAttribute('data-trix-attribute')
    const action = button.getAttribute('data-trix-action')
    const key = attribute || action
    
    if (!key) return
    
    console.log(`Applying fallback icon for ${key} button`)
    
    // Map of button types to their text representation
    const iconMap = {
      'bold': 'B',
      'italic': 'I',
      'strike': 'S',
      'link': '🔗',
      'heading1': 'H1',
      'quote': '"',
      'code': '</>',
      'pre': '</>',
      'bullet': '•',
      'number': '1.',
      'decreaseNestingLevel': '←',
      'increaseNestingLevel': '→',
      'attachFiles': '📎',
      'undo': '↩',
      'redo': '↪'
    }
    
    // Special handling for buttons that don't have standard attributes
    let iconContent = null;
    
    // Handle special cases
    if (button.classList.contains('trix-button--icon-code')) {
      iconContent = '</>';
    }
    
    // Get or create fallback icon element
    let fallbackIcon = button.querySelector('.trix-fallback-icon')
    if (!fallbackIcon) {
      fallbackIcon = document.createElement('span')
      fallbackIcon.classList.add('trix-fallback-icon')
      button.appendChild(fallbackIcon)
    }
    
    // Position and style the fallback icon
    fallbackIcon.style.position = 'absolute'
    fallbackIcon.style.top = '0'
    fallbackIcon.style.left = '0'
    fallbackIcon.style.right = '0'
    fallbackIcon.style.bottom = '0'
    fallbackIcon.style.display = 'flex'
    fallbackIcon.style.alignItems = 'center'
    fallbackIcon.style.justifyContent = 'center'
    fallbackIcon.style.fontWeight = 'bold'
    fallbackIcon.style.fontSize = '14px'
    
    // Set icon content
    if (iconContent) {
      fallbackIcon.innerHTML = iconContent;
    } else {
      const buttonKey = key.replace(/-/g, '')
      if (iconMap[buttonKey]) {
        fallbackIcon.innerHTML = iconMap[buttonKey]
      } else {
        // Default fallback
        fallbackIcon.innerHTML = key.charAt(0).toUpperCase()
        console.log(`No icon mapping found for ${key}`);
      }
    }
    
    // Ensure button has relative positioning for absolute icon positioning
    button.style.position = 'relative'
    
    // Add accessible labels
    button.setAttribute('aria-label', this.humanizeString(key))
    button.setAttribute('title', this.humanizeString(key))
  }
  
  humanizeString(str) {
    // Convert camelCase or kebab-case to Title Case with spaces
    return str
      .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
      .replace(/-/g, ' ') // Replace hyphens with spaces
      .replace(/^\w/, c => c.toUpperCase()) // Capitalize first letter
      .trim()
  }
  
  // Event Handlers
  onTrixInitialize(event) {
    console.log("Trix initialized event", event.target)
    
    // Force apply our custom styling
    if (this.element.contains(event.target)) {
      this.styleEditor(event.target)
      
      // Get the toolbar associated with this editor and fix its buttons
      const toolbar = event.target.previousElementSibling
      if (toolbar && toolbar.tagName === "TRIX-TOOLBAR") {
        this.fixToolbarButtons(toolbar)
      }
      
      // Fix attachment buttons that may already be present
      this.fixAttachmentButtons()
    }
  }
  
  onTrixFocus(event) {
    if (this.element.contains(event.target)) {
      // Add focus styling
      event.target.classList.add("trix-focused")
    }
  }
  
  onTrixBlur(event) {
    if (this.element.contains(event.target)) {
      // Remove focus styling
      event.target.classList.remove("trix-focused")
    }
  }
  
  onTrixFileAccept(event) {
    // Optional: Validate file type and size
    const acceptedTypes = ["image/jpeg", "image/png", "image/gif"]
    const maxFileSize = 5 * 1024 * 1024 // 5MB
    
    if (!acceptedTypes.includes(event.file.type)) {
      event.preventDefault()
      alert("Only JPEG, PNG, and GIF files are supported")
    } else if (event.file.size > maxFileSize) {
      event.preventDefault()
      alert("File must be less than 5MB")
    }
  }
  
  onTrixAttachmentAdd(event) {
    // Style attachment UI buttons when an attachment is added
    this.fixAttachmentButtons()
    
    if (this.element.contains(event.target)) {
      console.log("Attachment added", event.attachment)
    }
  }
  
  onTurboLoad() {
    // Re-initialize on Turbo navigation
    this.initializeEditor()
  }
} 